// The hold marks: what an own hold and a squadmate hold wear under the
// chart symbol (R185, R834, ui-map-interface.md §6.2).
//
// The Marks body of a theme carries two fields here. `ownHold` picks the
// mark on the commander's own hold and `squadHold` picks the mark on a
// squadmate's hold, and every value comes from mockup round twelve
// (`archive/mockups/012-friendly-markers.html`, series C and D).
//
// **The AUTO pair is the plate.** `ground-plate` (card C01) is the hexagon
// with a white rim and `half-plate` (card D03) is the same hexagon at a
// lower weight with no rim. Both are `drawPlate` of `render/marks.ts`.
// Every ALTERNATE replaces that plate; a value never stacks on it.
//
// **A still part goes into the hold's own cell.** The atlas bakes the hold
// as one cell a symbol, a reading, a faction, a rank and a mark, so a still
// part carries the faction ink and costs the frame nothing. It goes down
// BEFORE the symbol, which is the plate order of R185: the hull sits on top
// of the thing that marks it.
//
// **Only an own hold animates** (§6.2). A part that moves is one cell of
// its own, `site:holdanim:<value>:<part>`, drawn beside the beacon light.
// The cell is baked WHITE and the quad carries the tint, so a part says
// whether it takes the faction ink or stays white. Three values animate:
// `beam-column` (the hairline inside its column), `breathing-lozenge` and
// `landing-pillars`.
//
// **A gradient is baked as alpha bands.** `MarkGraphics` carries no
// gradient, so the two columns of cards C02 and D05 are a stack of
// `columnBands` trapezoid fills whose alpha falls to zero at the head. The
// stack is baked one time into an atlas cell.
//
// **A squad mark draws in the faction ink.** §6.2 holds that the white
// parts belong to the own hold alone, so `waterline-ticks` takes the
// faction ink where card D04 draws white. Card D03 dropped card C01's
// white rim for the same reason.
//
// This is a leaf (`leaves.test.ts`): it holds the shapes, reads its figures
// from the generated pack and imports no engine. The layer that draws it is
// `fast/layers/sites.ts` and the module that bakes the cells is
// `fast/gfx/atlas.ts`.

import { MARK_INKS, SITE_HOLD_MARKS } from '../gen/presentation';
import { drawPlate, type MarkGraphics } from './marks';
import type { OwnHoldStyle, SquadHoldStyle } from '../ui/settings';

/** The key of one baked cell of a part that moves. */
export const holdAnimKey = (style: string, part: string) => `site:holdanim:${style}:${part}`;

/** Which ink a part wears: white, or the faction of the hold. */
export type HoldMarkTint = 'white' | 'faction';

/**
 * One cell of a hold mark that moves.
 *
 * The cell is baked white and still. `alpha` and `scale` are the card's own
 * functions of the layer's clock, in seconds, and the layer writes them on
 * the quad every frame.
 */
export interface HoldAnimPart {
  key: string;
  tint: HoldMarkTint;
  /** Lays the path down and paints it white. The cell carries no alpha. */
  draw(g: MarkGraphics): void;
  /** The alpha at time `t`, in seconds. */
  alpha(t: number): number;
  /** The scale at time `t`. A part with no swell leaves it out. */
  scale?(t: number): number;
}

/** One value of the `ownHold` field or of the `squadHold` field. */
export interface HoldRankMark {
  /** What the hold's own baked cell carries, in the faction `ink`. */
  still(g: MarkGraphics, ink: number): void;
  /** The cells drawn every frame, in draw order. Absent when none moves. */
  anim?: readonly HoldAnimPart[];
}

const H = SITE_HOLD_MARKS;
/** The white a still part paints in its own colour: a dark core in the
 *  light identity (R3296). */
const WHITE = MARK_INKS.white;
/** The white of a part that moves: its cell is a mask, and the quad tints it. */
const MASK = 0xffffff; // mask

// ---------------------------------------------------------------------------
// The small shapes of the mockup page
// ---------------------------------------------------------------------------

/**
 * One chevron: a point at (0, `cy`) whose arms fall back by `h`.
 *
 * It is the `chevron` helper of the mockup page, with the paint left to the
 * caller so a crown of three lays three paths and pays three strokes.
 */
function chevron(g: MarkGraphics, cy: number, halfW: number, h: number): void {
  g.moveTo(-halfW, cy + h).lineTo(0, cy).lineTo(halfW, cy + h);
}

/** The four corners of a lozenge at radius `r`, squashed to `k` of height. */
function lozenge(cy: number, r: number, k: number): number[] {
  return [0, cy - r * k, r, cy, 0, cy + r * k, -r, cy];
}

/**
 * A column of light standing off the mast, as `columnBands` alpha bands.
 *
 * Band `i` is the trapezoid between two heights of the column. Its alpha is
 * the column's alpha at the middle of the band, falling linearly to zero at
 * the head, which is the linear gradient the card declares.
 */
function column(
  g: MarkGraphics, c: { y0: number; y1: number; halfW0: number; halfW1: number; alpha: number },
  ink: number,
): void {
  const n = H.columnBands;
  for (let i = 0; i < n; i++) {
    const t0 = i / n, t1 = (i + 1) / n;
    const y0 = c.y0 + (c.y1 - c.y0) * t0, y1 = c.y0 + (c.y1 - c.y0) * t1;
    const w0 = c.halfW0 + (c.halfW1 - c.halfW0) * t0, w1 = c.halfW0 + (c.halfW1 - c.halfW0) * t1;
    const a = c.alpha * (1 - (t0 + t1) / 2);
    g.poly([-w0, y0, w0, y0, w1, y1, -w1, y1]).fill({ color: ink, alpha: a });
  }
}

/** A value that draws nothing at all: card D01, and the animated values. */
function nothing(): void { /* the mark is the animated cell, or there is none */ }

// ---------------------------------------------------------------------------
// The still parts
// ---------------------------------------------------------------------------

/** Card C01 and card D03: the plate of R185. */
const groundPlate = (g: MarkGraphics, ink: number) => drawPlate(g, ink, true);
const halfPlate = (g: MarkGraphics, ink: number) => drawPlate(g, ink, false);

/** Card C02, the column. The hairline inside it is the animated part. */
function beamColumnStill(g: MarkGraphics, ink: number): void {
  column(g, H.beamColumn, ink);
}

/**
 * Card C04. Three chevrons stack over the hold and point down at it.
 *
 * The card paints the TOP chevron white and the two under it in the faction
 * ink, and it takes the alpha down at every step. The blurb of the card
 * says "brightest at the bottom"; the card's own draw code is the spec, and
 * it draws the brightest chevron at the top.
 */
function crownChevrons(g: MarkGraphics, ink: number): void {
  const f = H.crownChevrons;
  for (let i = 0; i < f.alphas.length; i++) {
    chevron(g, f.dy + i * f.pitch, f.halfW, f.h);
    g.stroke({
      width: f.w, color: i === 0 ? WHITE : ink, alpha: f.alphas[i],
      cap: 'round', join: 'round',
    });
  }
}

/** Card C05. Four rays leave the hold on the screen axes. */
function crossRays(g: MarkGraphics): void {
  const f = H.crossRays;
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    g.moveTo(dx * f.r0, f.cy + dy * f.r0).lineTo(dx * f.r1, f.cy + dy * f.r1);
  }
  g.stroke({ width: f.w, color: WHITE, alpha: f.alpha, cap: 'round' });
}

/** Card D02. One chevron over the dome: the bottom third of card C04. */
function singleChevron(g: MarkGraphics, ink: number): void {
  const f = H.singleChevron;
  chevron(g, f.dy, f.halfW, f.h);
  g.stroke({ width: f.w, color: ink, alpha: f.alpha, cap: 'round', join: 'round' });
}

/** Card D04. Two ticks at the foundation line, one each side. */
function waterlineTicks(g: MarkGraphics, ink: number): void {
  const f = H.waterlineTicks;
  for (const s of [-1, 1] as const) {
    g.moveTo(s * f.x0, f.dy).lineTo(s * f.x1, f.dy);
  }
  g.stroke({ width: f.w, color: ink, alpha: f.alpha, cap: 'round' });
}

/** Card D05. Card C02's column at a third of the height, and still. */
function shortColumn(g: MarkGraphics, ink: number): void {
  column(g, H.shortColumn, ink);
}

/** Card D08. Card C09's diamond, small, still and above the mast. */
function smallLozenge(g: MarkGraphics, ink: number): void {
  const f = H.smallLozenge;
  g.poly(lozenge(f.cy, f.r, 1)).stroke({ width: f.w, color: ink, alpha: f.alpha, join: 'round' });
}

// ---------------------------------------------------------------------------
// The parts that move
// ---------------------------------------------------------------------------

/** Card C02, the hairline. It breathes inside the still column. */
const beamLine: HoldAnimPart = {
  key: holdAnimKey('beam-column', 'line'),
  tint: 'white',
  draw(g) {
    const f = H.beamLine;
    g.moveTo(0, f.y0).lineTo(0, f.y1).stroke({ width: f.w, color: MASK });
  },
  alpha: (t) => H.beamLine.alphaBase + H.beamLine.alphaSwing * Math.sin(t * H.beamLine.hz),
};

/**
 * Card C09. The breathing ring of R90 with the circle taken out of it.
 *
 * The squash goes into the baked points and the swell rides the quad,
 * because the quad carries one scale and the card scales the two axes by
 * the same factor over the squash.
 */
const breathingLozenge: HoldAnimPart = {
  key: holdAnimKey('breathing-lozenge', 'ring'),
  tint: 'white',
  draw(g) {
    const f = H.breathingLozenge;
    g.poly(lozenge(f.cy, f.r, f.squash)).stroke({ width: f.w, color: MASK, join: 'round' });
  },
  alpha(t) {
    const f = H.breathingLozenge;
    return f.alphaBase + f.alphaSwing * (0.5 + 0.5 * Math.sin(t * f.hz));
  },
  scale: (t) => 1 + H.breathingLozenge.scaleSwing * Math.sin(t * H.breathingLozenge.hz),
};

/**
 * Card C10. One of the four bars either side of the dome.
 *
 * `side` is -1 for the left bar and 1 for the right one, and the two blink
 * a half period apart. The glow bar is the wider, fainter one in the
 * faction ink, and it is drawn UNDER its own bar so the bar stays hard.
 */
function pillar(side: -1 | 1, glow: boolean): HoldAnimPart {
  const f = H.landingPillars;
  const phase = side < 0 ? 0 : Math.PI;
  const name = `${side < 0 ? 'left' : 'right'}${glow ? '-glow' : ''}`;
  return {
    key: holdAnimKey('landing-pillars', name),
    tint: glow ? 'faction' : 'white',
    draw(g) {
      const w = glow ? f.glowW : f.barW;
      const y = glow ? f.glowY : f.barY;
      const h = glow ? f.glowH : f.barH;
      const x = side * f.dx;
      g.poly([x - w / 2, y, x + w / 2, y, x + w / 2, y + h, x - w / 2, y + h])
        .fill({ color: MASK });
    },
    alpha(t) {
      const a = f.alphaBase + f.alphaSwing * Math.abs(Math.sin(t * f.hz + phase));
      return glow ? a * f.glowAlpha : a;
    },
  };
}

const landingPillars: readonly HoldAnimPart[] = [
  pillar(-1, true), pillar(-1, false), pillar(1, true), pillar(1, false),
];

// ---------------------------------------------------------------------------
// The two tables
// ---------------------------------------------------------------------------

/**
 * The mark on the commander's own hold, per value of the `ownHold` field.
 *
 * `ground-plate` is the shipped look and the AUTO value. Every ALTERNATE
 * replaces the plate: the hold keeps its symbol, its text and its light,
 * and the value alone says the hold is the commander's own.
 */
export const OWN_HOLD_MARKS: Record<OwnHoldStyle, HoldRankMark> = {
  'ground-plate': { still: groundPlate },
  'beam-column': { still: beamColumnStill, anim: [beamLine] },
  'crown-chevrons': { still: crownChevrons },
  'cross-rays': { still: crossRays },
  'breathing-lozenge': { still: nothing, anim: [breathingLozenge] },
  'landing-pillars': { still: nothing, anim: landingPillars },
};

/**
 * The mark on a squadmate's hold, per value of the `squadHold` field.
 *
 * `half-plate` is the shipped look and the AUTO value. `no-mark` (card D01)
 * is the null option: a squadmate's hold is already a faction-coloured base
 * and the shared coverage ring of Comms 1 says where it is. No value here
 * animates, because only an own hold animates (§6.2).
 */
export const SQUAD_HOLD_MARKS: Record<SquadHoldStyle, HoldRankMark> = {
  'half-plate': { still: halfPlate },
  'no-mark': { still: nothing },
  'single-chevron': { still: singleChevron },
  'waterline-ticks': { still: waterlineTicks },
  'short-column': { still: shortColumn },
  'small-lozenge': { still: smallLozenge },
};

/**
 * Every cell that moves, of both tables, by key.
 *
 * The atlas resolver reads this: a cell is baked on the first frame that
 * draws it, so a page bakes only the value the reader picked.
 */
export const HOLD_ANIM_PARTS: ReadonlyMap<string, HoldAnimPart> = new Map(
  [...Object.values(OWN_HOLD_MARKS), ...Object.values(SQUAD_HOLD_MARKS)]
    .flatMap((s) => s.anim ?? [])
    .map((p) => [p.key, p]),
);

/** Is `id` a value of the `ownHold` field? */
export function isOwnHoldMark(id: string): id is OwnHoldStyle {
  return Object.hasOwn(OWN_HOLD_MARKS, id);
}

/** Is `id` a value of the `squadHold` field? */
export function isSquadHoldMark(id: string): id is SquadHoldStyle {
  return Object.hasOwn(SQUAD_HOLD_MARKS, id);
}
